import { FOOTER_PREMIUM } from "../common/footer";
import { MARKETING_HEADER } from "../common/header";

/**
 * World-Class Re-engagement Template
 * Features: Welcome back offer, new features, activity summary, compelling CTAs
 */
export const reEngagement = `
<mjml>
  <mj-head>
    <mj-title>We Miss You, {{first_name}}! Here's What's New</mj-title>
    <mj-preview>Come back and see what you've been missing</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .comeback-badge { background: linear-gradient(135deg, #EF4444, #DC2626); color: white; padding: 8px 20px; border-radius: 25px; font-size: 13px; font-weight: 700; }
      .new-badge { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#fef2f2">
    \${MARKETING_HEADER("We Miss You!", "It's been a while", "#EF4444")}

    <!-- Welcome Back Message -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="28px" color="#0f172a" font-weight="700" align="center" padding-bottom="12px">
          Hey {{first_name}}, it's been {{days_inactive}} days!
        </mj-text>
        <mj-text font-size="15px" color="#64748b" align="center">
          We noticed you haven't logged in for a while, and we wanted to check in. A lot has changed since your last visit, and we think you'll love the new features.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Special Offer Banner -->
    <mj-section background-color="#EF4444" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="rgba(255,255,255,0.8)" text-transform="uppercase" letter-spacing="2px" align="center" padding-bottom="10px">
          Welcome Back Offer
        </mj-text>
        <mj-text font-size="56px" color="#ffffff" font-weight="800" align="center" padding="0" line-height="1">
          {{discount_percentage}}% OFF
        </mj-text>
        <mj-text font-size="16px" color="rgba(255,255,255,0.9)" align="center" padding-top="10px" padding-bottom="15px">
          Use code: <span style="background: rgba(255,255,255,0.2); padding: 6px 16px; border-radius: 6px; font-weight: 700; letter-spacing: 2px;">{{discount_code}}</span>
        </mj-text>
        <mj-text font-size="13px" color="rgba(255,255,255,0.7)" align="center">
          Valid until {{offer_expiry}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- What's New Section -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#EF4444" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Updates
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          What's New Since You Left
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 1 -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <span style="background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700;">NEW</span>
          <strong style="margin-left: 12px; font-size: 17px; color: #0f172a;">{{feature_1_name}}</strong><br><br>
          <span style="color: #64748b; font-size: 14px;">{{feature_1_description}}</span>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 2 -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <span style="background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700;">NEW</span>
          <strong style="margin-left: 12px; font-size: 17px; color: #0f172a;">{{feature_2_name}}</strong><br><br>
          <span style="color: #64748b; font-size: 14px;">{{feature_2_description}}</span>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Feature 3 -->
    <mj-section background-color="#ffffff" padding="10px 30px 30px" border-radius="0 0 16px 16px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <span style="background: linear-gradient(135deg, #3B82F6, #2563EB); color: white; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 700;">IMPROVED</span>
          <strong style="margin-left: 12px; font-size: 17px; color: #0f172a;">{{feature_3_name}}</strong><br><br>
          <span style="color: #64748b; font-size: 14px;">{{feature_3_description}}</span>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Activity Summary -->
    <mj-section background-color="#f0f9ff" padding="30px" border-radius="16px" border="2px solid #bae6fd">
      <mj-column>
        <mj-text font-size="18px" color="#0f172a" font-weight="700" padding-bottom="15px">
          &#129302; Your Chatbots Are Waiting
        </mj-text>
        <mj-text font-size="14px" color="#64748b" padding-bottom="15px">
          While you were away, your chatbots handled <strong style="color: #0EA5E9;">{{conversations_while_away}}</strong> conversations. Here's a quick summary:
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px 0; color: #64748b; font-size: 14px;">Total Messages</td>
            <td style="padding: 12px 0; text-align: right; font-weight: 700; color: #0EA5E9; font-size: 16px;">{{messages_while_away}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px 0; color: #64748b; font-size: 14px;">Leads Captured</td>
            <td style="padding: 12px 0; text-align: right; font-weight: 700; color: #10B981; font-size: 16px;">{{leads_while_away}}</td>
          </tr>
          <tr>
            <td style="padding: 12px 0; color: #64748b; font-size: 14px;">Satisfaction Rate</td>
            <td style="padding: 12px 0; text-align: right; font-weight: 700; color: #8B5CF6; font-size: 16px;">{{satisfaction_while_away}}%</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Ready to Jump Back In?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Your chatbots are running, but there's so much more you could do.
        </mj-text>
        <mj-button background-color="#2DD4BF" color="#0F172A" href="{{dashboard_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          Log In Now &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Help Section -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="15px" color="#64748b" align="center">
          <strong>Having trouble?</strong> Hit reply and let us know how we can help you get back on track.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "reEngagement",
  name: "Re-engagement - Win Back",
  category: "sequences",
  purpose: "sequence" as const,
  description:
    "A world-class re-engagement email with bold welcome-back discount, new features showcase, activity summary while away, and compelling return CTAs.",
  features: [
    "Bold discount banner with code",
    "New features showcase cards",
    "Activity summary while away",
    "Personalized inactivity mention",
    "Multiple return CTAs",
    "Premium footer with support",
  ],
  subject: "We Miss You, {{first_name}}! Here's {{discount_percentage}}% Off &#128149;",
  subTitle: "Come back and see what you've been missing",
  mjmlTemplate: reEngagement,
  tags: ["sequence", "re-engagement", "win-back", "inactive", "offer"],
};
